/**
 * Frontend-only metadata about role packages.
 *
 * The backend returns only a role package's name and description — the badge color and the
 * can-see / can-do bullet lists do not exist in the API and are maintained here. The key is the
 * role package's *English* name as it arrives from the backend. New roles fall through to a gray
 * "—" row until they are added here.
 *
 * The values are translation keys, not display text — resolve them via TranslateService or
 * TranslatePipe.
 */
export interface RoleMeta {
  /** Translated display name. The backend's own `name` is English-only, so it is not shown. */
  displayName: string;
  /** Translated description. Overrides the backend's English `description`. */
  description: string;
  levelLabel: string;
  levelColor: string;
  canSee: string[];
  canDo: string[];
  cannotSee?: string[];
  cannotDo?: string[];
  /**
   * Scope advice shown as a muted footer under the role card. Optional: the design only spells one
   * out for some roles, and a card without advice simply renders no footer rather than filler.
   */
  note?: string;
  /**
   * Set where the role's can-see / can-do lists describe capabilities the platform does not have —
   * alarm handling, support tickets, remote device control. The card carries a pill saying so, since
   * the lists otherwise read as a description of what the role does today.
   */
  notImplemented?: true;
}

/** A role package joined with its metadata, ready for display. */
export interface RoleDisplay {
  id: number;
  /** Translated display name. */
  name: string;
  /**
   * The backend's English name, kept alongside the translated one because it is the key into
   * `ROLE_METADATA` — `app-role-badge` needs it to resolve the color and label itself.
   */
  rawName: string | null;
  description: string | null;
  levelLabel: string;
  levelColor: string;
  canSee: string[];
  canDo: string[];
  cannotSee?: string[] | undefined;
  cannotDo?: string[] | undefined;
  note?: string | undefined;
  notImplemented?: true | undefined;
}

export const ROLE_METADATA: Record<string, RoleMeta> = {
  'System Administrator': {
    displayName: 'role_sysadmin_name', description: 'role_sysadmin_desc',
    levelLabel: 'role_sysadmin_level', levelColor: 'red',
    canSee: ['role_sysadmin_see_1', 'role_sysadmin_see_2', 'role_sysadmin_see_3'],
    canDo:  ['role_sysadmin_do_1',  'role_sysadmin_do_2',  'role_sysadmin_do_3'],
    note:   'role_sysadmin_note',
  },
  'Administrator': {
    displayName: 'role_admin_name', description: 'role_admin_desc',
    levelLabel: 'role_admin_level', levelColor: 'orange',
    canSee:    ['role_admin_see_1', 'role_admin_see_2', 'role_admin_see_3'],
    cannotSee: ['role_admin_cannot_see_1'],
    canDo:     ['role_admin_do_1', 'role_admin_do_2', 'role_admin_do_3'],
    cannotDo:  ['role_admin_cannot_do_1'],
    note:      'role_admin_note',
    notImplemented: true,
  },
  'Technician': {
    displayName: 'role_tech_name', description: 'role_tech_desc',
    levelLabel: 'role_tech_level', levelColor: 'green',
    canSee: ['role_tech_see_1', 'role_tech_see_2', 'role_tech_see_3'],
    canDo:  ['role_tech_do_1',  'role_tech_do_2',  'role_tech_do_3'],
    note:   'role_tech_note',
    notImplemented: true,
  },
  'Support': {
    displayName: 'role_support_name', description: 'role_support_desc',
    levelLabel: 'role_support_level', levelColor: 'blue',
    canSee: ['role_support_see_1', 'role_support_see_2'],
    canDo:  ['role_support_do_1',  'role_support_do_2',  'role_support_do_3'],
    note:   'role_support_note',
    notImplemented: true,
  },
  'Full Read': {
    displayName: 'role_fullread_name', description: 'role_fullread_desc',
    levelLabel: 'role_fullread_level', levelColor: 'purple',
    canSee:    ['role_fullread_see_1', 'role_fullread_see_2', 'role_fullread_see_3'],
    cannotSee: ['role_fullread_cannot_see_1'],
    canDo:     ['role_fullread_do_1'],
    cannotDo:  ['role_fullread_cannot_do_1', 'role_fullread_cannot_do_2', 'role_fullread_cannot_do_3'],
    note:      'role_fullread_note',
  },
};

/** Badge color for a role package. Unknown roles fall back to gray. */
export function roleColorFor(roleName: string | null | undefined): string {
  return ROLE_METADATA[roleName ?? '']?.levelColor ?? 'gray';
}

/**
 * Translation key for a role package's display name, or null for a role not in the map.
 *
 * Call sites hold the backend's English `name` (`UserResponseDto.rolePackageName`,
 * `RolePackageResponseDto.name`) and must never render it directly — fall back to the raw name only
 * when this returns null, so an unmapped new role still shows something rather than a blank.
 */
export function roleNameKey(roleName: string | null | undefined): string | null {
  return ROLE_METADATA[roleName ?? '']?.displayName ?? null;
}

/**
 * Translation key for a role package's description, or null for a role not in the map.
 *
 * Null rather than a placeholder is what lets `app-role-badge` omit its tooltip entirely for an
 * unmapped role instead of showing a raw key.
 */
export function roleDescriptionKey(roleName: string | null | undefined): string | null {
  return ROLE_METADATA[roleName ?? '']?.description ?? null;
}
